import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Save, FilePlus, Trash2, Printer, X, Receipt, CreditCard,
  Calendar, User, Hash, FileText, Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReceiptPaymentVoucherPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // type=receipt (قبض) | payment (صرف)
  const [voucherType, setVoucherType] = useState(
    searchParams.get("type") === "payment" ? "payment" : "receipt"
  );
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().slice(0, 10));
  const [organizerName, setOrganizerName] = useState(user?.name || user?.email || "");
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");
  // ✨ نوع الحساب: عميل أو مورد (سند الصرف افتراضياً للمورد)
  const [accountType, setAccountType] = useState(
    searchParams.get("type") === "payment" ? "supplier" : "customer"
  );
  const [accountLabel, setAccountLabel] = useState("");
  const [movementType, setMovementType] = useState("");
  const [exchangeRate, setExchangeRate] = useState(1470);
  const [amountUsd, setAmountUsd] = useState("");
  const [amountIqd, setAmountIqd] = useState("");
  const [description, setDescription] = useState("");
  const [protectionLevel, setProtectionLevel] = useState("medium");
  const [voucherNumber, setVoucherNumber] = useState("");

  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [recentVouchers, setRecentVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const isReceipt = voucherType === "receipt";
  const titleAr = isReceipt ? "سند قبض" : "سند صرف";
  const accentColor = isReceipt ? "emerald" : "rose";

  // ===== Fetch =====
  const fetchNextNumber = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/simple-vouchers/next-number?voucher_type=${voucherType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoucherNumber(res.data.next_number);
    } catch (e) {
      // ignore
    }
  }, [voucherType, token]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {/*ignore*/}
  }, [token]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {/*ignore*/}
  }, [token]);

  const fetchRecent = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/simple-vouchers?voucher_type=${voucherType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecentVouchers((res.data?.vouchers || []).slice(0, 8));
    } catch (e) {/*ignore*/}
  }, [voucherType, token]);

  useEffect(() => { fetchCustomers(); fetchSuppliers(); }, [fetchCustomers, fetchSuppliers]);
  useEffect(() => { fetchNextNumber(); fetchRecent(); }, [fetchNextNumber, fetchRecent]);

  // ===== Helpers =====
  const totalIqdEquiv = (parseFloat(amountIqd) || 0) + ((parseFloat(amountUsd) || 0) * exchangeRate);
  const balanceIqd = totalIqdEquiv;
  const balanceUsd = exchangeRate > 0 ? totalIqdEquiv / exchangeRate : 0;

  const resetForm = () => {
    setCustomerName("");
    setCustomerId("");
    setAccountLabel("");
    setMovementType("");
    setAmountUsd("");
    setAmountIqd("");
    setDescription("");
    setProtectionLevel("medium");
    setVoucherDate(new Date().toISOString().slice(0, 10));
    fetchNextNumber();
  };

  const onEntitySelect = (eid) => {
    setCustomerId(eid);
    const list = accountType === "supplier" ? suppliers : customers;
    const e = list.find((x) => x.id === eid);
    if (e) {
      setCustomerName(e.name);
      setAccountLabel(e.name);
    }
  };

  const handleSave = async () => {
    if (!amountUsd && !amountIqd) {
      toast.error("أدخل المبلغ بالدينار أو الدولار");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        voucher_type: voucherType,
        voucher_date: new Date(voucherDate).toISOString(),
        customer_name: customerName || null,
        customer_id: customerId || null,
        account_type: accountType,  // ✨ customer | supplier
        account_label: accountLabel || customerName || null,
        movement_type: movementType || (isReceipt ? "قبض" : "صرف"),
        exchange_rate: parseFloat(exchangeRate) || 1470,
        amount_usd: parseFloat(amountUsd) || 0,
        amount_iqd: parseFloat(amountIqd) || 0,
        description: description || null,
        protection_level: protectionLevel,
        organizer_name: organizerName || null,
      };
      const res = await axios.post(`${API}/simple-vouchers`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`✅ تم حفظ السند ${res.data.voucher_number}`);
      resetForm();
      fetchRecent();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "فشل الحفظ";
      toast.error(`فشل الحفظ: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف هذا السند؟")) return;
    try {
      await axios.delete(`${API}/simple-vouchers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم الحذف");
      fetchRecent();
    } catch (err) {
      toast.error("فشل الحذف");
    }
  };

  const handlePrint = () => {
    if (!voucherNumber || (!amountUsd && !amountIqd)) {
      toast.error("احفظ السند أولاً ثم اطبع");
      return;
    }
    window.print();
  };

  // Keyboard shortcuts: F3 Save, F4 New
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F3") { e.preventDefault(); handleSave(); }
      if (e.key === "F4") { e.preventDefault(); resetForm(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountUsd, amountIqd, voucherType, customerName]);

  return (
    <div className="min-h-screen bg-slate-950 p-4" dir="rtl">
      {/* Type Tabs */}
      <div className="max-w-6xl mx-auto mb-4 flex gap-2">
        <Button
          onClick={() => setVoucherType("receipt")}
          className={voucherType === "receipt"
            ? "flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-base py-6"
            : "flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-base py-6 border border-slate-700"}
          data-testid="voucher-type-receipt-btn"
        >
          <Receipt className="w-5 h-5 ms-2" />
          سند قبض (المال يدخل)
        </Button>
        <Button
          onClick={() => setVoucherType("payment")}
          className={voucherType === "payment"
            ? "flex-1 bg-rose-600 hover:bg-rose-700 text-white text-base py-6"
            : "flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-base py-6 border border-slate-700"}
          data-testid="voucher-type-payment-btn"
        >
          <CreditCard className="w-5 h-5 ms-2" />
          سند صرف (المال يخرج)
        </Button>
      </div>

      <Card className={`max-w-6xl mx-auto bg-slate-900 border-${accentColor}-500/30`}>
        <CardHeader className={`bg-gradient-to-l from-${accentColor}-500/20 to-transparent`}>
          <CardTitle className="text-white text-2xl flex items-center justify-between">
            <span className="flex items-center gap-2">
              {isReceipt ? <Receipt className={`w-6 h-6 text-${accentColor}-400`} />
                          : <CreditCard className={`w-6 h-6 text-${accentColor}-400`} />}
              {titleAr}
            </span>
            <span className="text-sm text-slate-400 font-mono" data-testid="voucher-number-display">
              {voucherNumber}
            </span>
          </CardTitle>
        </CardHeader>

        <CardContent className="p-5 space-y-4">
          {/* Top row: protection / date / organizer */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs flex items-center gap-1">
                <Shield className="w-3 h-3" />مستوى الحماية
              </Label>
              <Select value={protectionLevel} onValueChange={setProtectionLevel}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="voucher-protection-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="weak" className="text-white">🔓 ضعيف</SelectItem>
                  <SelectItem value="medium" className="text-white">🔐 متوسط</SelectItem>
                  <SelectItem value="strong" className="text-white">🛡️ قوي</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs flex items-center gap-1">
                <Calendar className="w-3 h-3" />تاريخ الوصل
              </Label>
              <Input
                type="date"
                value={voucherDate}
                onChange={(e) => setVoucherDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="voucher-date-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs flex items-center gap-1">
                <User className="w-3 h-3" />منظم الوصل
              </Label>
              <Input
                value={organizerName}
                onChange={(e) => setOrganizerName(e.target.value)}
                placeholder="اسم الموظف"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="voucher-organizer-input"
              />
            </div>
          </div>

          {/* Customer/Supplier + voucher number */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs flex items-center gap-1">
                <User className="w-3 h-3" />
                {accountType === "supplier" ? "اسم المورد" : "اسم العميل"}
              </Label>
              {/* ✨ تبديل نوع الحساب */}
              <div className="flex gap-1 mb-1">
                <Button
                  type="button"
                  size="sm"
                  variant={accountType === "customer" ? "default" : "outline"}
                  onClick={() => {
                    setAccountType("customer");
                    setCustomerId("");
                    setCustomerName("");
                    setAccountLabel("");
                  }}
                  className={accountType === "customer" ? "bg-emerald-600 hover:bg-emerald-700 text-xs h-7" : "border-slate-600 text-slate-300 text-xs h-7"}
                  data-testid="account-type-customer"
                >
                  👤 عميل
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={accountType === "supplier" ? "default" : "outline"}
                  onClick={() => {
                    setAccountType("supplier");
                    setCustomerId("");
                    setCustomerName("");
                    setAccountLabel("");
                  }}
                  className={accountType === "supplier" ? "bg-amber-600 hover:bg-amber-700 text-xs h-7" : "border-slate-600 text-slate-300 text-xs h-7"}
                  data-testid="account-type-supplier"
                >
                  🏭 مورد
                </Button>
              </div>
              <div className="flex gap-2">
                <Select value={customerId} onValueChange={onEntitySelect}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white flex-1" data-testid="voucher-customer-select">
                    <SelectValue placeholder={accountType === "supplier" ? "اختر مورد" : "اختر عميل"} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-72">
                    {(accountType === "supplier" ? suppliers : customers).length === 0 && (
                      <div className="px-3 py-2 text-slate-500 text-sm">
                        {accountType === "supplier"
                          ? "لا يوجد موردين — أضفهم من صفحة الموردون"
                          : "لا يوجد عملاء — أضفهم من صفحة الزبائن"}
                      </div>
                    )}
                    {(accountType === "supplier" ? suppliers : customers).map((c) => (
                      <SelectItem key={c.id} value={c.id} className="text-white">
                        {c.name} {c.phone ? `(${c.phone})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={customerName}
                  onChange={(e) => { setCustomerName(e.target.value); setCustomerId(""); }}
                  placeholder="أو اكتب الاسم"
                  className="bg-slate-800 border-slate-700 text-white flex-1"
                  data-testid="voucher-customer-name-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs flex items-center gap-1">
                <Hash className="w-3 h-3" />رقم الوصل
              </Label>
              <Input
                value={voucherNumber}
                readOnly
                className="bg-slate-900 border-slate-700 text-amber-300 font-mono"
                data-testid="voucher-number-input"
              />
            </div>
          </div>

          {/* Movement + exchange + amounts + balances */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 pt-2 border-t border-slate-700">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">نوع الحركة</Label>
              <Input
                value={movementType}
                onChange={(e) => setMovementType(e.target.value)}
                placeholder={isReceipt ? "قبض" : "صرف"}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="voucher-movement-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">سعر الصرف</Label>
              <Input
                type="number"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0)}
                className="bg-slate-800 border-slate-700 text-white font-mono"
                data-testid="voucher-rate-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">المبلغ $</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={amountUsd}
                onChange={(e) => setAmountUsd(e.target.value)}
                placeholder="0"
                className="bg-slate-800 border-slate-700 text-white font-mono"
                data-testid="voucher-amount-usd-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">المبلغ دينار</Label>
              <Input
                type="number"
                inputMode="numeric"
                value={amountIqd}
                onChange={(e) => setAmountIqd(e.target.value)}
                placeholder="0"
                className="bg-slate-800 border-slate-700 text-white font-mono"
                data-testid="voucher-amount-iqd-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">الرصيد $</Label>
              <Input
                value={balanceUsd ? balanceUsd.toFixed(2) : "0"}
                readOnly
                className="bg-slate-900 border-slate-700 text-cyan-300 font-mono"
                data-testid="voucher-balance-usd"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">الرصيد د.ع</Label>
              <Input
                value={balanceIqd ? balanceIqd.toLocaleString() : "0"}
                readOnly
                className="bg-slate-900 border-slate-700 text-cyan-300 font-mono"
                data-testid="voucher-balance-iqd"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1 pt-2 border-t border-slate-700">
            <Label className="text-slate-300 text-xs flex items-center gap-1">
              <FileText className="w-3 h-3" />البيان
            </Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="تفاصيل السند..."
              className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              data-testid="voucher-description-input"
            />
          </div>

          {/* Action bar — F3 Save / F4 New / Print / Exit */}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-700">
            <Button
              onClick={handleSave}
              disabled={loading}
              className={`bg-${accentColor}-600 hover:bg-${accentColor}-700 text-white`}
              data-testid="voucher-save-btn"
            >
              <Save className="w-4 h-4 ms-2" />
              حفظ (F3)
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="voucher-new-btn"
            >
              <FilePlus className="w-4 h-4 ms-2" />
              جديد (F4)
            </Button>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-blue-600 text-blue-400 hover:bg-blue-500/10"
              data-testid="voucher-print-btn"
            >
              <Printer className="w-4 h-4 ms-2" />
              طباعة
            </Button>
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="voucher-exit-btn"
            >
              <X className="w-4 h-4 ms-2" />
              خروج
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent vouchers list */}
      <Card className="max-w-6xl mx-auto mt-4 bg-slate-900/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">آخر سندات {titleAr}</CardTitle>
        </CardHeader>
        <CardContent>
          {recentVouchers.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">لا توجد سندات بعد</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recent-vouchers-table">
                <thead className="text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="text-right py-2 px-2">الرقم</th>
                    <th className="text-right py-2 px-2">التاريخ</th>
                    <th className="text-right py-2 px-2">العميل</th>
                    <th className="text-right py-2 px-2">المبلغ ($)</th>
                    <th className="text-right py-2 px-2">المبلغ (د.ع)</th>
                    <th className="text-right py-2 px-2">البيان</th>
                    <th className="text-center py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {recentVouchers.map((v) => (
                    <tr key={v.id} className="border-b border-slate-800 text-slate-200 hover:bg-slate-800/40">
                      <td className="py-2 px-2 font-mono text-amber-300">{v.voucher_number}</td>
                      <td className="py-2 px-2 text-xs">{(v.voucher_date || "").slice(0, 10)}</td>
                      <td className="py-2 px-2">{v.customer_name || "—"}</td>
                      <td className="py-2 px-2 font-mono">{v.amount_usd ? `$${v.amount_usd}` : "—"}</td>
                      <td className="py-2 px-2 font-mono">{v.amount_iqd ? v.amount_iqd.toLocaleString() : "—"}</td>
                      <td className="py-2 px-2 text-xs text-slate-400 truncate max-w-[180px]">{v.description || "—"}</td>
                      <td className="py-2 px-2 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(v.id)}
                          className="text-rose-400 hover:bg-rose-500/10 h-7 px-2"
                          data-testid={`voucher-delete-${v.id}-btn`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
